'use client';
import React, { useState } from 'react';
import { MapPin, Download, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { PublicSearchResult } from './PublicSearchPortal';

interface Props {
  results: PublicSearchResult[];
  query: string;
}

export default function PublicSearchResults({ results, query }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (results.length === 0) {
    return (
      <div className="card-base p-10 text-center shadow-card mt-0">
        <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center mx-auto mb-4">
          <Search size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-base font-600 text-foreground">No records found for "{query}"</h3>
        <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
          No burial records match your search. Please check the spelling, try a different name, or contact the municipal office for assistance.
        </p>
        <div className="mt-4 p-3 bg-[#fffbeb] border border-warning/30 rounded-lg inline-block">
          <p className="text-xs text-warning font-600">
            Records prior to 2020 may not be available online. Contact the cemetery office directly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-0">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground font-500">
          <span className="font-700 text-foreground">{results.length}</span> record{results.length !== 1 ? 's' : ''} found for "{query}"
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
        {results.map((r) => (
          <div
            key={r.id}
            className="card-base shadow-card overflow-hidden hover:shadow-elevated transition-shadow cursor-pointer"
            onClick={() => setExpanded(expanded === r.id ? null : r.id)}
          >
            {/* Card Header */}
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white text-sm font-700">
                  {r.fullName.split(' ').map((n) => n[0]).slice(0, 2).join('')}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-positive bg-[#f0fdf4] px-2 py-0.5 rounded-full font-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-positive" />
                  Record Found
                </div>
              </div>

              <h3 className="text-base font-700 text-foreground">{r.fullName}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {r.dateOfBirth} – {r.dateOfDeath}
              </p>

              <div className="mt-3 space-y-1.5">
                <div className="flex items-center gap-2 text-xs">
                  <MapPin size={12} className="text-primary shrink-0" />
                  <span className="text-foreground font-500">{r.cemetery}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-mono-data font-600 text-primary">{r.graveId}</span>
                  <span>·</span>
                  <span>Block {r.block}, Row {r.row}, Plot {r.plotNumber}</span>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-muted/50 rounded-lg px-2 py-1.5">
                  <p className="text-muted-foreground">Burial Date</p>
                  <p className="font-600 text-foreground mt-0.5">{r.burialDate}</p>
                </div>
                <div className="bg-muted/50 rounded-lg px-2 py-1.5">
                  <p className="text-muted-foreground">Section</p>
                  <p className="font-600 text-foreground mt-0.5">{r.section}</p>
                </div>
              </div>
            </div>

            {/* Expanded Details */}
            {expanded === r.id && (
              <div className="border-t border-border px-5 py-4 bg-secondary/30 slide-up">
                <div className="space-y-2 mb-4">
                  {[
                    ['ID Number', r.idNumber],
                    ['Date of Birth', r.dateOfBirth],
                    ['Date of Death', r.dateOfDeath],
                    ['Cemetery', r.cemetery],
                    ['Grave Reference', r.graveId],
                  ].map(([label, value]) => (
                    <div key={`pub-detail-${r.id}-${label}`} className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{label}</span>
                      <span className={`font-600 ${label === 'ID Number' || label === 'Grave Reference' ? 'font-mono-data' : ''}`}>
                        {value}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Cemetery Map Placeholder */}
                <div className="w-full h-24 bg-muted rounded-lg border border-border flex items-center justify-center mb-3">
                  <div className="text-center">
                    <MapPin size={16} className="text-primary mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">Cemetery map view</p>
                    <p className="text-[11px] text-muted-foreground">{r.block} / {r.row} / Plot {r.plotNumber}</p>
                  </div>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toast.success(`Burial certificate for ${r.fullName} downloaded`);
                  }}
                  className="btn-primary w-full justify-center text-sm"
                >
                  <Download size={14} /> Download Burial Certificate
                </button>
                <p className="text-[11px] text-muted-foreground text-center mt-2">
                  For certified copies, visit the Nyandeni Municipal offices
                </p>
              </div>
            )}

            {/* Expand toggle */}
            <div className="px-5 pb-3 flex items-center justify-between">
              <span className="text-xs text-primary font-600">
                {expanded === r.id ? 'Show less' : 'View details & download'}
              </span>
              <ChevronRight
                size={14}
                className={`text-primary transition-transform duration-200 ${expanded === r.id ? 'rotate-90' : ''}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}